import { useRef } from 'react';
import { useAppChatStore } from '@/entities/AppChat/model/store';
import { useChatDialogStore } from '@/entities/ChatDialog/model/store';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { chatInboxFilterSchema } from '../model/schema';
import type { ChatInboxFilterValues } from '../model/types';
import { createChatInboxFilterValues } from '../mappers';
import { useSearchParams } from 'react-router-dom';
import type { ChatDialogResponse } from '@/core/generated/api';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestEmpty } from '@/shared/ui/RequestState';
import { AppChatWorkspace } from '@/features/AppChat/ui/AppChatWorkspace';
import { ChatDialogWorkspace } from '@/features/ChatDialog/Workspace/ChatDialogWorkspace';
import { ChatDialogList } from '@/features/ChatDialog/Workspace/components/ChatDialogList';
import { useChatInbox } from '../model/useChatInbox';
import { ChatInboxFilters } from './ChatInboxFilters';

/** Показывает общую переписку приложения и подключённых мессенджеров */
export const ChatInboxWorkspace = (
    { initialSource = 'native' }: { initialSource?: 'native' | 'salebot' }
) => {
    const native = useCanAccessPermission('admin.chats.view');
    const external = useCanAccessPermission('admin.chat-dialogs.view');
    const readNative = useCanAccessPermission('admin.chats.read');
    const readExternal = useCanAccessPermission('admin.chat-dialogs.read');
    const [params, setParams] = useSearchParams();
    const listScrollPosition = useRef(0);
    const inbox = useChatInbox(native, external);

    const filterForm = useForm<ChatInboxFilterValues>({
        resolver: zodResolver(chatInboxFilterSchema),
        defaultValues: createChatInboxFilterValues(),
    });

    const requested = params.get('source') ?? initialSource;

    const source = requested === 'native' && inbox.nativeAvailable || !external
        ? 'native'
        : 'salebot';

    /** Закрывает выбранный диалог */
    const close = (): void => {
        listScrollPosition.current = 0;

        setParams((previous) => {
            const next = new URLSearchParams(previous);
            next.delete('dialogId');

            return next;
        });
    };

    /** Открывает переписку с правилами выбранного канала */
    const select = (dialog: ChatDialogResponse): void => {
        if (!dialog.id) return;

        setParams((previous) => {
            const next = new URLSearchParams(previous);
            next.set('dialogId', dialog.id ?? '');

            next.set('source', dialog.channel?.id === 'vox'
                ? 'native'
                : 'salebot');

            return next;
        });
    };

    /** Проверяет право изменения чтения выбранного канала */
    const canChangeReadState = (dialog: ChatDialogResponse): boolean => dialog.channel?.id === 'vox'
        ? readNative
        : readExternal;

    /** Закрывает помечаемую переписку до завершения автоматического прочтения */
    const changeReadState = async (dialog: ChatDialogResponse, unread: boolean): Promise<void> => {
        if (!canChangeReadState(dialog)) return;

        if (unread && params.get('dialogId') === dialog.id) {
            const store = dialog.channel?.id === 'vox'
                ? useAppChatStore
                : useChatDialogStore;

            store.getState().clearSelection();
            close();
        }

        await inbox.setReadState(dialog, unread);
    };

    if (!native && !external) return <RequestEmpty message="Нет доступа к переписке" />;

    if (native && !inbox.nativeAvailable && !external) {
        return <RequestEmpty message="Чаты приложения отключены в настройках сервера" />;
    }

    const list = <ChatDialogList
        scrollPosition={listScrollPosition}
        state={{
            ...inbox.list,
            selectedDialogId: params.get('dialogId') ?? undefined
        }}
        onSelect={select}
        onPin={inbox.togglePin}
        pinPending={inbox.pinPending}
        onReadState={readNative || readExternal
            ? changeReadState
            : undefined
        }
        canChangeReadState={canChangeReadState}
        readPending={inbox.readPending}
        onClose={close}
        filterControl={<ChatInboxFilters
            form={filterForm}
            onApply={(filter) => {
                close();
                inbox.apply(filter);
            }}
        />}
    />;

    return source === 'native' && native
        ? <AppChatWorkspace
            listContent={list}
        />
        : <ChatDialogWorkspace
            listContent={list}
        />;
};
